/**
 * @module NotificationTypeBadge
 * @package @geenius/notifications-solidjs
 * @description Renders a small semantic badge for a notification type in the
 * Tailwind SolidJS variant.
 */

import { motion } from '@geenius/motion/solidjs'
import type { NotificationType } from '@geenius/notifications/shared'
import { getNotificationTypeLabel, TYPE_CONFIG } from '@geenius/notifications/shared'
import type { JSX } from 'solid-js'
import { createNotificationCopy } from '../lib/notificationCopy'
import {
  getNotificationAccentStyle,
  notificationTypeBadgeVariants,
} from '../lib/notificationStyles'
import { cn } from '../lib/utils'

/**
 * Props for the notification type badge.
 */
export interface NotificationTypeBadgeProps {
  type: NotificationType
  class?: string
}

/**
 * Renders a semantic badge for a notification type.
 *
 * @param props Badge props.
 * @returns The rendered badge.
 */
export function NotificationTypeBadge(
  props: NotificationTypeBadgeProps,
): JSX.Element {
  const { t } = createNotificationCopy()
  const config = () => TYPE_CONFIG[props.type]

  return (
    <motion.span
      style={getNotificationAccentStyle(props.type)}
      whileHover={{ y: -1, scale: 1.02 }}
      class={cn(notificationTypeBadgeVariants(), props.class)}
    >
      {config().icon} {getNotificationTypeLabel(props.type, t)}
    </motion.span>
  )
}
